// The chat-channel -> coordinator turn-gate bridge (`beekeeper-progress-indicator` §3; UI.md
// §4B), pulled out of `RoundsController.tsx` into its own module deliberately: this hook's own
// dependency closure is three things — React, the `ChatSeam` contract, and the coordinator's
// public type — and importing `RoundsController.tsx` to reach it drags in that file's whole
// transitive graph, including build-time inlined binary assets (`ambient-loop.bin?inline` via
// `ui/home/ambient-asset.ts`) that a plain `vitest run` of a single test file cannot transform
// outside the app's own Vite pipeline. A unit test for a small pure hook should not need the
// asset pipeline at all, so the hook lives where its test can import it alone.

import { useEffect, useRef } from 'react';
import { isChatBusy, type ChatSeam } from '../../ui/colony/chat-seam';
import type { SandboxCoordinator } from '../../ui/rounds/sandbox-coordinator';

/**
 * **The chat-channel → coordinator turn-gate bridge** (`beekeeper-progress-indicator` §3;
 * UI.md §4B). The coordinator already has `turn-started`/`turn-settled` events and the reducer
 * already handles both (`sandbox-coordinator.ts`'s `turn-settled` case), but nothing ever
 * dispatched the first one — so before this bridge the gate only ever engaged after a reload
 * landed directly inside a running turn (`colonyMeta.turnInFlight`'s boot seed), never from a
 * live send. **Level-triggered**: every reconcile compares the channel's current busy-ness
 * against the coordinator's current `turnInFlight` and corrects any mismatch, rather than trying
 * to remember "did I raise this" — the transition itself is never the signal, only the level is.
 * That is what makes a missed notification unable to latch the gate shut: the very next
 * reconcile (any subsequent chat snapshot) re-derives the right answer from scratch, it does not
 * extend a remembered edge.
 *
 * Also where `rehydrate()` is called, folded in rather than left as a bare
 * `useEffect(() => { void chat?.rehydrate(); }, [chat])` beside this one: the bridge has to know
 * the EXACT moment hydration answers to implement the readiness rule below, and `rehydrate()`'s
 * own internal `publish()` (or, on a total fetch failure, its absence) fires synchronously inside
 * that same promise — a sibling effect's own `.then()` would already be a tick too late to catch
 * it reliably. Folding the call in here is also what delivers `practiceChat.rehydrate()` "on
 * practice boot, mirroring the tournament call": each side gets its own `useTurnGateBridge`
 * instance, so each side's channel is rehydrated exactly once, from exactly one place.
 *
 * **Hydration readiness** (review F2): before this channel's `rehydrate()` has resolved once,
 * this bridge does nothing at all. A tournament coordinator can boot with `turnInFlight: true`
 * seeded straight from `colonyMeta` (`RoundsController.tsx`'s coordinator construction) while the
 * channel itself still answers its inert default `idle` — an async `rehydrate()` has not
 * finished. Reconciling off that default before hydration answers would drop a real turn's gate
 * for the few ticks hydration takes.
 *
 * **Adoption, not bare ownership** (review F3): once hydration answers, this bridge's very first
 * reconcile is exactly the same "compare and correct" as every later one — there is no separate
 * "adopt" code path. That is what closes the latch bug a bare "only clear what I raised" ref has:
 * reload into a running turn finds `busy === true` and `turnInFlight` already `true` from the
 * boot seed, so THIS reconcile dispatches nothing (correct — nothing to correct) but the very
 * next one (the turn settling, busy → false) compares against the CURRENT `turnInFlight` — still
 * `true` — and dispatches `turn-settled`, precisely because nothing here depends on whether an
 * earlier reconcile happened to raise it. The one thing a raw "only clear what I raised" ref
 * cannot do (settle a turn that moves no document, since the coordinator's other clearer —
 * `colonyMeta`'s diff effect — fires only on a document move, and is disabled in practice) falls
 * out for free.
 *
 * **The readiness rule is an asymmetry, not a blanket wait** (`beekeeper-progress-indicator`
 * review F1). It reads above as "before hydration completes, this bridge does nothing at all" —
 * true when this bridge first shipped, but wrong once the channel itself could construct
 * synchronously into a busy phase (`uncertain`, restored from a persisted marker read at
 * construction — `chat-channel.ts`'s own C1). Waiting for hydration in THAT case cost a full
 * network round trip during which a real submission's gate stayed open: Run, version selection
 * and hand editing all stayed live, even though the composer itself was already correctly locked
 * from the channel's synchronous snapshot.
 *
 * The fix is not to drop the readiness rule — its reason is still real — but to see that the
 * reason only ever protected *clearing*. Raising the gate needs no evidence beyond "the channel
 * says busy right now": there is no boot sequence in which a busy channel plus an open gate is
 * ever the correct state, hydrated or not, so a raise dispatches unconditionally, including
 * synchronously at mount for a channel that is already busy before `rehydrate()` has even been
 * called. *Clearing* is the one direction that can be wrong from an unconfirmed reading — a
 * coordinator can boot with the gate raised from `colonyMeta` while the channel's own default is
 * still the inert `idle` it has not yet had a chance to correct — so clearing alone stays gated on
 * `hydratedRef.current`.
 */
export function useTurnGateBridge(chat: ChatSeam | undefined, coordinator: SandboxCoordinator | null): void {
  const hydratedRef = useRef(false);

  useEffect(() => {
    // A fresh coordinator identity (a new practice scope opening, or the tournament one's first
    // build) starts hydration-readiness over: whatever the PREVIOUS coordinator's channel had
    // answered says nothing about this one's `turnInFlight` boot seed.
    hydratedRef.current = false;
    if (chat === undefined || coordinator === null) {
      return;
    }
    const reconcile = (): void => {
      const busy = isChatBusy(chat.getSnapshot().phase);
      const held = coordinator.getState().turnInFlight;
      if (busy === held) {
        return;
      }
      if (busy) {
        // Raising needs no evidence beyond the channel's own current word — safe unconditionally,
        // hydrated or not (F1 above).
        coordinator.dispatch({ kind: 'turn-started' });
        return;
      }
      // Clearing is the direction that can be wrong from an unconfirmed reading (review F2's
      // readiness rule, narrowed here to the branch it actually protects): before this channel's
      // `rehydrate()` has resolved once, `idle` might just be the inert default rather than a
      // confirmed fact, and clearing on it would drop a real turn's gate for the few ticks
      // hydration takes.
      if (!hydratedRef.current) {
        return;
      }
      coordinator.dispatch({ kind: 'turn-settled', document: null });
    };
    let cancelled = false;
    // A channel that already reads busy at construction — the synchronous `uncertain` restore
    // from a persisted marker — must raise the gate immediately, before `rehydrate()` below has
    // even resolved once (F1). Safe unconditionally per the asymmetry above.
    reconcile();
    void chat.rehydrate().then(() => {
      if (cancelled) {
        return;
      }
      hydratedRef.current = true;
      reconcile();
    });
    const unsubscribe = chat.subscribe(reconcile);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [chat, coordinator]);
}
